import {useParams} from "react-router-dom";
import {useEffect} from "react";
import {useShows} from "store/show/show.store";
import {shallow} from "zustand/shallow";
import {
  IPayloadUpdatedShow,
  IShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import {apiV2} from "api/firebase";
import {INotificationBaseMessage} from "@evennt-tech/api-v2-core/dist/notification/notification-base-message.interface";
import {SHOWS_PATH} from "view/routes";

type UseCurrentShowResult = [IShow | undefined, boolean];

type UseCurrentShowOptions = {
  showId?: string;
  enabled?: boolean;
};

const useCurrentShow = (
  options: UseCurrentShowOptions = {}
): UseCurrentShowResult => {
  const {showId: providedShowId, enabled = true} = options;

  const {showId: routeShowId} = useParams();
  const showId = providedShowId ?? routeShowId ?? "";

  const {showFromApi, fetchOne, isLoadingApi} = useShows(
    (state) => ({
      showFromApi: state.selected,
      fetchOne: state.fetchOne,
      isLoadingApi: state.isLoading,
    }),
    shallow
  );

  const redirectToRoot = () => {
    window.location.replace(SHOWS_PATH);
  };

  useEffect(() => {
    if (!enabled || !showId) return;
    fetchOne({showId});
    apiV2.notification.subscribe<INotificationBaseMessage<IPayloadUpdatedShow>>(
      {
        key: NotificationType.DELETED_SHOW,
        callback: async ({payload}) => {
          if (!payload) return;

          const {showId: deletedShowId} = payload;

          if (deletedShowId === showId) {
            redirectToRoot();
          }
        },
      }
    );
  }, [enabled, showId, fetchOne]);

  useEffect(() => {
    if (showFromApi && showFromApi?.deletedAt) {
      redirectToRoot();
    }
  }, [showFromApi, isLoadingApi]);

  const loading = enabled && isLoadingApi;

  return [showFromApi as IShow | undefined, loading];
};

export default useCurrentShow;
